import { api } from '../client';
import type { PageParams } from '@/types/api';
import type { AcademicStatus, UUID } from '@/types/domain';
import type {
  AcademicTerm,
  Examination,
  ExaminationStatus,
  ExaminationType,
  ExamSchedule,
  GradingScale,
  MarkStatus,
  MarksSheet,
  StudentResult,
} from '@/types/examination';

export interface ExaminationPayload {
  gradeOfferingId: UUID;
  termId?: UUID;
  examinationTypeId: UUID;
  code: string;
  name: string;
  description?: string;
  startDate: string;
  endDate: string;
  gradingScaleId?: UUID;
}

export interface ExaminationSubjectPayload {
  academicSubjectId: UUID;
  maxMarks: number;
  passingMarks: number;
  assessmentComponentId?: UUID;
  displayOrder?: number;
}

export const examinationsApi = {
  list: (
    params: PageParams & {
      gradeOfferingId?: UUID;
      termId?: UUID;
      academicYearId?: UUID;
      status?: ExaminationStatus;
    },
  ) => api.getPage<Examination>('/examinations', { params }),

  get: (id: UUID) => api.get<Examination>(`/examinations/${id}`),

  create: (payload: ExaminationPayload) => api.post<Examination>('/examinations', payload),

  /**
   * The grade offering is fixed after creation — it decides which children sat
   * the paper, so moving it would change the meaning of every recorded mark.
   * The form disables that field rather than the API silently ignoring it.
   */
  update: (id: UUID, payload: ExaminationPayload) =>
    api.put<Examination>(`/examinations/${id}`, payload),

  /** Only the transitions the backend permits are accepted; the UI mirrors them, it does not decide them. */
  changeStatus: (id: UUID, status: ExaminationStatus) =>
    api.patch<Examination>(`/examinations/${id}/status`, undefined, { params: { status } }),

  addSubject: (id: UUID, payload: ExaminationSubjectPayload) =>
    api.post<Examination>(`/examinations/${id}/subjects`, payload),

  removeSubject: (id: UUID, subjectId: UUID) =>
    api.delete<Examination>(`/examinations/${id}/subjects/${subjectId}`),
};

export interface ExaminationTypePayload {
  code: string;
  name: string;
  description?: string;
  defaultWeightage?: number;
  displayOrder?: number;
}

export const examinationTypesApi = {
  list: () => api.get<ExaminationType[]>('/examination-types'),
  create: (payload: ExaminationTypePayload) =>
    api.post<ExaminationType>('/examination-types', payload),
  update: (id: UUID, payload: ExaminationTypePayload) =>
    api.put<ExaminationType>(`/examination-types/${id}`, payload),
  changeStatus: (id: UUID, payload: { status: AcademicStatus; reason?: string }) =>
    api.patch<ExaminationType>(`/examination-types/${id}/status`, payload),
};

export interface ExamSchedulePayload {
  examinationSubjectId: UUID;
  sectionId: UUID;
  examDate: string;
  startTime: string;
  endTime: string;
  room?: string;
  invigilatorTeacherId?: UUID;
  instructions?: string;
}

export const examScheduleApi = {
  list: (examinationId: UUID) =>
    api.get<ExamSchedule[]>(`/examinations/${examinationId}/schedule`),

  /** Rejected when the section, room or invigilator is already occupied at that hour. */
  create: (examinationId: UUID, payload: ExamSchedulePayload) =>
    api.post<ExamSchedule>(`/examinations/${examinationId}/schedule`, payload),

  update: (examinationId: UUID, scheduleId: UUID, payload: ExamSchedulePayload) =>
    api.put<ExamSchedule>(`/examinations/${examinationId}/schedule/${scheduleId}`, payload),

  remove: (examinationId: UUID, scheduleId: UUID) =>
    api.delete<void>(`/examinations/${examinationId}/schedule/${scheduleId}`),
};

export interface MarksEntryPayload {
  sectionId: UUID;
  entries: {
    enrollmentId: UUID;
    /** Omitted for absent and exempt — a zero is a score, not an absence. */
    obtainedMarks?: number;
    markStatus: MarkStatus;
    remarks?: string;
  }[];
}

export const marksApi = {
  sheet: (examinationSubjectId: UUID, sectionId: UUID) =>
    api.get<MarksSheet>(`/marks/${examinationSubjectId}`, { params: { sectionId } }),
  save: (examinationSubjectId: UUID, payload: MarksEntryPayload) =>
    api.put<MarksSheet>(`/marks/${examinationSubjectId}`, payload),
  submit: (examinationSubjectId: UUID, sectionId: UUID) =>
    api.post<MarksSheet>(`/marks/${examinationSubjectId}/submit`, undefined, {
      params: { sectionId },
    }),
  review: (
    examinationSubjectId: UUID,
    /** Omit the section to act on every submitted sheet for the paper. */
    payload: { sectionId?: UUID; approved: boolean; comment?: string },
  ) => api.post<MarksSheet>(`/marks/${examinationSubjectId}/review`, payload),
};

export const resultsApi = {
  list: (examinationId: UUID) =>
    api.get<StudentResult[]>(`/examinations/${examinationId}/results`),
  forStudent: (examinationId: UUID, enrollmentId: UUID) =>
    api.get<StudentResult>(`/examinations/${examinationId}/results/${enrollmentId}`),
  calculate: (examinationId: UUID) =>
    api.post<{ calculated: number }>(`/examinations/${examinationId}/results/calculate`),
  publish: (examinationId: UUID) =>
    api.post<{ published: number }>(`/examinations/${examinationId}/results/publish`),
  unpublish: (examinationId: UUID) =>
    api.post<{ withdrawn: number }>(`/examinations/${examinationId}/results/unpublish`),
};

export const gradingScalesApi = {
  list: () => api.get<GradingScale[]>('/grading-scales'),
  get: (id: UUID) => api.get<GradingScale>(`/grading-scales/${id}`),
};

export const academicTermsApi = {
  forYear: (academicYearId: UUID) =>
    api.get<AcademicTerm[]>('/academic-terms', { params: { academicYearId } }),
};
